import * as React from "react"
import { CalendarIcon, Clock } from "lucide-react"
import dayjs, { Dayjs } from "dayjs"
import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface DateTimePickerProps {
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  className?: string;
  disablePastDates?: boolean;
  maxDate?: Date;
  isScheduled?: boolean;
}

export function DateTimePicker({
  value,
  onChange,
  label,
  error,
  helperText,
  className,
  disablePastDates = false,
  maxDate,
  isScheduled = false
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(
    value ? value.toDate() : undefined
  )
  const [time, setTime] = React.useState<string>(
    value ? value.format("HH:mm") : ""
  )

  React.useEffect(() => {
    if (value) {
      setDate(value.toDate())
      setTime(value.format("HH:mm"))
    } else {
      setDate(undefined)
      setTime("")
    }
  }, [value])

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      setDate(undefined)
      onChange(null)
      return
    }

    setDate(selectedDate)
    
    // Combine date and time
    if (time) {
      const [hours, minutes] = time.split(":").map(Number)
      const combinedDate = new Date(selectedDate)
      combinedDate.setHours(hours || 0, minutes || 0, 0, 0)
      
      // If disablePastDates is enabled and the selected date/time is in the past, reset to now
      if (disablePastDates) {
        const now = new Date()
        if (combinedDate < now) {
          const newTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0')
          setTime(newTime)
          onChange(dayjs(now))
          return
        }
      }
      
      onChange(dayjs(combinedDate))
    } else {
      // If no time set, use current time if today, otherwise midnight
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const selectedDateStart = new Date(selectedDate)
      selectedDateStart.setHours(0, 0, 0, 0)
      
      if (disablePastDates && selectedDateStart.getTime() === today.getTime()) {
        // If today is selected, use current time
        const now = new Date()
        const newTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0')
        setTime(newTime)
        onChange(dayjs(now))
      } else {
        // If future date, use midnight
        const combinedDate = new Date(selectedDate)
        combinedDate.setHours(0, 0, 0, 0)
        onChange(dayjs(combinedDate))
      }
    }
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value
    setTime(newTime)

    if (date && newTime) {
      const [hours, minutes] = newTime.split(":").map(Number)
      const combinedDate = new Date(date)
      combinedDate.setHours(hours || 0, minutes || 0, 0, 0)
      
      // If disablePastDates is enabled and the selected date/time is in the past, prevent it
      if (disablePastDates) {
        const now = new Date()
        if (combinedDate < now) {
          // Reset to current time
          const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0')
          setTime(currentTime)
          onChange(dayjs(now))
          return
        }
      }
      
      onChange(dayjs(combinedDate))
    } else if (date && !newTime) {
      // If time is cleared but date exists, keep the date with current time
      onChange(dayjs(date))
    }
  }

  const displayValue = React.useMemo(() => {
    if (!value) return ""
    return value.format("MMM DD, YYYY HH:mm")
  }, [value])

  return (
    <div className={cn("space-y-2", className, isScheduled && "use-primary-color")}>
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className={cn("relative", isScheduled && "use-primary-color")}>
            <Input
              readOnly
              value={displayValue}
              placeholder="Pick a date and time"
              className={cn(
                "w-full pl-10 pr-10",
                isScheduled 
                  ? "border border-secondary focus-visible:ring-secondary focus-visible:ring-2"
                  : "border-0 focus-visible:ring-0",
                error && "border-destructive",
                !value && "text-muted-foreground"
              )}
              onClick={() => setOpen(true)}
            />
            <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            {value && (
              <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className={cn("w-auto p-0", isScheduled && "use-primary-color")} align="start">
          <div className="p-3 space-y-3">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              initialFocus
              disabled={(date) => {
                if (disablePastDates) {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (date < today) return true;
                }
                if (maxDate && date > maxDate) return true;
                return false;
              }}
              isScheduled={isScheduled}
            />
            <div className="border-t pt-3">
              <Label className="text-xs mb-2 block">Time</Label>
              <Input
                type="time"
                value={time}
                onChange={handleTimeChange}
                className={cn(
                  "w-full",
                  isScheduled && "focus-visible:ring-secondary focus-visible:ring-2"
                )}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {helperText && (
        <p className={cn("text-sm", error ? "text-destructive" : "text-muted-foreground")}>
          {helperText}
        </p>
      )}
    </div>
  )
}

